import React from 'react';

/*
 * Reusable circular gauge (speedometer / RPM) built with SVG.
 * 240deg sweep from -120deg to +120deg.
 */
export const GaugeDial = ({
  value = 0,
  min = 0,
  max = 60,
  majorStep = 10,
  label = 'SPEED',
  unit = 'km/h',
  redlineStart = null,
  needleColor = 'hsl(18 100% 55%)',
  size = 200,
  testId,
}) => {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 14;
  const startAngle = -120;
  const endAngle = 120;
  const sweep = endAngle - startAngle;
  const clamped = Math.max(min, Math.min(max, value));
  const frac = (clamped - min) / (max - min);
  const needleAngle = startAngle + frac * sweep;

  const polar = (angleDeg, radius) => {
    const a = (angleDeg - 90) * (Math.PI / 180);
    return { x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) };
  };

  const arcPath = (a0, a1, radius) => {
    const p0 = polar(a0, radius);
    const p1 = polar(a1, radius);
    const large = a1 - a0 > 180 ? 1 : 0;
    return `M ${p0.x} ${p0.y} A ${radius} ${radius} 0 ${large} 1 ${p1.x} ${p1.y}`;
  };

  const ticks = [];
  const count = Math.round((max - min) / majorStep);
  for (let i = 0; i <= count; i++) {
    const tv = min + i * majorStep;
    const ta = startAngle + ((tv - min) / (max - min)) * sweep;
    const outer = polar(ta, r);
    const inner = polar(ta, r - 12);
    const lbl = polar(ta, r - 26);
    ticks.push({ tv, outer, inner, lbl });
  }

  const needle = polar(needleAngle, r - 18);

  return (
    <div className="flex flex-col items-center justify-center w-full h-full min-h-0" data-testid={testId}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        preserveAspectRatio="xMidYMid meet"
        className="block w-full h-full min-h-0"
        style={{ maxWidth: size, maxHeight: size }}
      >
        <defs>
          <radialGradient id={`face-${label}`} cx="50%" cy="60%" r="60%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.10)" />
            <stop offset="55%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
        </defs>
        {/* face */}
        <circle cx={cx} cy={cy} r={r + 8} fill="#14181f" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
        <circle cx={cx} cy={cy} r={r + 2} fill={`url(#face-${label})`} />
        {/* base arc */}
        <path d={arcPath(startAngle, endAngle, r)} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="4" strokeLinecap="round" />
        {/* value arc */}
        <path d={arcPath(startAngle, needleAngle, r)} fill="none" stroke="hsl(173 70% 42%)" strokeWidth="4" strokeLinecap="round" />
        {/* redline */}
        {redlineStart !== null && (
          <path
            d={arcPath(startAngle + ((redlineStart - min) / (max - min)) * sweep, endAngle, r)}
            fill="none"
            stroke="hsl(0 84% 60%)"
            strokeWidth="4"
            strokeLinecap="round"
          />
        )}
        {/* ticks */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={t.inner.x} y1={t.inner.y} x2={t.outer.x} y2={t.outer.y} stroke="rgba(255,255,255,0.65)" strokeWidth="2" />
            <text x={t.lbl.x} y={t.lbl.y} fill="rgba(255,255,255,0.75)" fontSize="11" textAnchor="middle" dominantBaseline="middle" fontFamily="'Bebas Neue', sans-serif">
              {t.tv}
            </text>
          </g>
        ))}
        {/* needle */}
        <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke={needleColor} strokeWidth="4" strokeLinecap="round" style={{ transition: 'all 0.08s linear' }} />
        <circle cx={cx} cy={cy} r="8" fill="#2b2f36" stroke={needleColor} strokeWidth="2" />
        {/* readout */}
        <text x={cx} y={cy + r - 30} fill="#fff" fontSize="30" textAnchor="middle" fontFamily="'Bebas Neue', sans-serif" className="tabular-nums">
          {Math.round(clamped)}
        </text>
        <text x={cx} y={cy + r - 12} fill="rgba(255,255,255,0.6)" fontSize="10" textAnchor="middle" fontFamily="'IBM Plex Mono', monospace">
          {unit}
        </text>
      </svg>
      <span className="font-display tracking-[0.18em] text-xs sm:text-sm text-white/70 -mt-1 shrink-0">{label}</span>
    </div>
  );
};
